/**
 * Development Configuration
 * 
 * SECRET MANAGEMENT STRATEGY:
 * - ALL environments (development, staging, production) use Google Cloud Secret Manager as PRIMARY
 * - Environment variables (.env files) serve as FALLBACK if Secret Manager fails
 * - This ensures consistent behavior across all deployment environments
 * 
 * DEVELOPMENT ENVIRONMENT SPECIFICS:
 * - Uses v1-courtly Google Cloud project
 * - Secrets prefixed with 'courtly-development-'
 * - Sample data enabled for demo/testing purposes
 * - Reduced session expiration and enhanced logging for development convenience
 */

import { USE_SAMPLE_DATA } from './env';

// =============================================================================
// DEVELOPMENT FLAGS
// =============================================================================

export const isDevelopment = process.env.NODE_ENV === 'development';
export const isProduction = process.env.NODE_ENV === 'production';

// Development-specific localStorage control - respect environment variable
export const DISABLE_LOCALSTORAGE_IN_DEV = process.env.NEXT_PUBLIC_DISABLE_LOCALSTORAGE === 'true';
export const ENABLE_FAST_EXPIRATION = process.env.NEXT_PUBLIC_FAST_EXPIRATION === 'true';

// =============================================================================
// DEVELOPMENT STORAGE CONFIGURATION
// =============================================================================

export interface DevelopmentStorageConfig {
  enableLocalStorage: boolean;
  enableSessionStorage: boolean;
  fastExpirationMode: boolean;
  sessionExpirationMinutes: number; // For testing - use minutes instead of days
  cleanupIntervalMs: number; // More frequent cleanup in dev
  enableDebugLogging: boolean;
}

export const getDevelopmentStorageConfig = (): DevelopmentStorageConfig => {
  if (!isDevelopment) {
    // Non-development environments (staging/production)
    // Both staging and production use real data only (no sample data)
    return {
      enableLocalStorage: true,
      enableSessionStorage: true,
      fastExpirationMode: false,
      sessionExpirationMinutes: 7 * 24 * 60, // 7 days for real data
      cleanupIntervalMs: 60 * 60 * 1000, // 1 hour
      enableDebugLogging: false,
    };
  }

  // Development configuration - only development uses sample data
  return {
    enableLocalStorage: !DISABLE_LOCALSTORAGE_IN_DEV,
    enableSessionStorage: true, // Keep sessionStorage for temporary data
    fastExpirationMode: ENABLE_FAST_EXPIRATION,
    sessionExpirationMinutes: USE_SAMPLE_DATA ? 120 : (ENABLE_FAST_EXPIRATION ? 1 : 120), // 2 hours for sample data, 1 minute for fast testing, 2 hours for normal dev
    cleanupIntervalMs: ENABLE_FAST_EXPIRATION ? 10000 : 60000, // 10 seconds for fast testing, 1 minute for normal dev
    enableDebugLogging: true,
  };
};



// =============================================================================
// DEVELOPMENT SESSION MANAGER CONFIG
// =============================================================================

export interface DevelopmentSessionConfig {
  enableLocalStoragePersistence: boolean;
  localStorageExpirationDays: number;
  enablePerformanceTracking: boolean;
  enableEventSystem: boolean;
  tokenRefreshThreshold: number; // minutes
}

export const getDevelopmentSessionConfig = (): DevelopmentSessionConfig => {
  const storageConfig = getDevelopmentStorageConfig();
  
  if (!isDevelopment) {
    // Non-development environments (staging/production)
    // Both staging and production use real data only (no sample data)
    return {
      enableLocalStoragePersistence: true,
      localStorageExpirationDays: 7, // 7 days for real data
      enablePerformanceTracking: true,
      enableEventSystem: true,
      tokenRefreshThreshold: 5,
    };
  }

  // Development configuration - only development uses sample data
  return {
    enableLocalStoragePersistence: storageConfig.enableLocalStorage,
    localStorageExpirationDays: USE_SAMPLE_DATA ? 2 : (storageConfig.fastExpirationMode ? 0.001 : 0.08), // 2 days for sample data, ~1.5 minutes or ~2 hours for dev
    enablePerformanceTracking: true,
    enableEventSystem: true,
    tokenRefreshThreshold: 1, // Refresh tokens more frequently in dev
  };
};

// =============================================================================
// DEVELOPMENT UTILITIES
// =============================================================================

/**
 * Log development configuration on startup
 */
export const logDevelopmentConfig = (): void => {
  if (!isDevelopment) return;

  const storageConfig = getDevelopmentStorageConfig();
  const sessionConfig = getDevelopmentSessionConfig();

  console.log('🔧 Development Configuration:');
  console.log('📦 Storage:', {
    localStorage: storageConfig.enableLocalStorage ? '✅ Enabled' : '❌ Disabled',
    sessionStorage: storageConfig.enableSessionStorage ? '✅ Enabled' : '❌ Disabled',
    fastExpiration: storageConfig.fastExpirationMode ? '⚡ Fast Mode' : '🐌 Normal Mode',
    sessionExpiration: `${storageConfig.sessionExpirationMinutes} minutes`,
    sampleDataMode: USE_SAMPLE_DATA ? '✅ Enabled' : '❌ Disabled',
  });
  console.log('🔐 Session:', {
    persistence: sessionConfig.enableLocalStoragePersistence ? '✅ Enabled' : '❌ Disabled',
    expiration: USE_SAMPLE_DATA ? '30 minutes' : `${sessionConfig.localStorageExpirationDays} days`,
  });
};

/**
 * Clear all development storage
 */
export const clearDevelopmentStorage = (): void => {
  if (!isDevelopment) return;

  try {
    localStorage.clear();
    sessionStorage.clear();
    console.log('🧹 Development storage cleared');
  } catch (error) {
    console.warn('Failed to clear development storage:', error);
  }
};

/**
 * Force expire all localStorage items for testing
 */
export const forceExpireLocalStorage = (): void => {
  if (!isDevelopment) return;

  try {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      try {
        const item = localStorage.getItem(key);
        if (item) {
          const parsed = JSON.parse(item);
          if (parsed.expiresAt) {
            // Set expiration to past
            parsed.expiresAt = Date.now() - 1000;
            localStorage.setItem(key, JSON.stringify(parsed));
          }
        }
      } catch {
        // Skip non-JSON items
      }
    });
    console.log('⏰ All localStorage items force-expired for testing');
  } catch (error) {
    console.warn('Failed to force expire localStorage:', error);
  }
};

// =============================================================================
// DEVELOPMENT ENVIRONMENT VARIABLES
// =============================================================================

/**
 * Development environment variables for localStorage testing
 * Add these to your .env.local file:
 * 
 * # Disable localStorage completely
 * NEXT_PUBLIC_DISABLE_LOCALSTORAGE=true
 * 
 * # Enable fast expiration (1 minute instead of 7 days)
 * NEXT_PUBLIC_FAST_EXPIRATION=true
 * 
 * # Enable debug logging
 * NEXT_PUBLIC_DEBUG_STORAGE=true
 */ 